import { ReactNode, useState } from 'react';
import { AlertTriangle, LogOut } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Sidebar from './Sidebar';
import Header from './Header';
import Footer from './Footer';
import ImpersonationBanner from './admin/ImpersonationBanner';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isImpersonating } = useImpersonation();
  const { tenant, allTenants, isLoadingTenants } = useTenant();
  const { user, logout } = useAuth();

  const hasNoTenantAccess = !isLoadingTenants && !user?.isMegaAdmin && allTenants.length === 0;

  if (hasNoTenantAccess) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
            {t('layout.no_access')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('layout.no_access_desc')}
          </p>
          <div className="space-y-3">
            <p className="text-sm text-gray-500 dark:text-gray-500">
              {t('layout.logged_in_as')} <span className="font-medium text-gray-700 dark:text-gray-300">{user?.email}</span>
            </p>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              {t('layout.sign_out')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900 overflow-hidden transition-colors duration-200">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden">
        {isImpersonating && <ImpersonationBanner />}
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 dark:bg-gray-900">
          <div className="p-4 sm:p-6">
            {children}
          </div>
          <Footer />
        </main>
      </div>
    </div>
  );
};

export default Layout;